import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Form } from 'antd';

import { CommerceQuotationFormByTab } from '../../components';

import QuotationService, { DEFAULT_PAYMENT_PLAN_CODE } from '../../../../../../../../../shared/services/quotation';

import { GenericDataFormContext, LOADING_SAVED_QUOTATIONS_STATES, QUOTATION_BUTTON, QuotationButton, QuotationInfoContext, QuotationStep, StepOneFormContext } from '../../../../../shared';
import { StepFourFormContext } from './StepFourFormContext';
import { StepThreeFormContext } from '../../../StepThree/contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';

import { CommerceQuotationComponent, CommerceQuotationRequest, PropertyFeature, WebCommerceQuotation } from '../../../../../../../../../shared/interfaces/services';
import { GetCommertialsConditionsResponse, QuotationCoverage } from '../../../../../../../../../shared/interfaces';

import { generateInputID, getCoverageAndActivityKeyByID } from '../../../../../AP/Steps/StepThree/components/Quotation/utils';

import { getValueDefaultPaymentMethod } from '../../../../../utils/get-value-default-payment-method';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { scrollToEmitButton } from '../../../../../../../../../shared/utils';

export interface CommerceQuotationForm { 
  [key: string]: string;
};

export interface CommercePaymentMethodForm {
  [key: string]:          string | number;
  paymentMethod:          string;
  comission:              number;
  extraVariable:          number;
  variation:              number;
};

export const StepFourFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [ stepFourCommerceQuotationForm ] = Form.useForm<CommerceQuotationForm>();
  const [ commercePaymentMethodsForm ] = Form.useForm<CommercePaymentMethodForm>();

  const {
    allLocalitiesPerProvince,
    paymentMethodsList,
    allCoveragesPerPlan,
    setCanShowSpinner,
    setPlansSelectedByTabs,
    updateFormsAfterRemoveTabs,
    setTabsUserCanNotDelete,
    removedTabIndexes, 
    setRemovedTabIndexes,
    formValues,
  } = useContext(GenericDataFormContext);
  const {
    quotationInfo,
    savedQuotation,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    setCurrentStep,
    setLoadingSavedQuotation,
    currentStep,
    setLoadingRenewalQuotation,
    quotationToRenewal,
    setRenewalQuotationState,
    renewalQuotationState
  } = useContext(QuotationInfoContext);
  const { clientSelected } = useContext( StepOneFormContext );
  const { handleTabErrors } = useContext( WizardDataFormContext );
  const { stepThreeForm, tabs, allFeatures } = useContext( StepThreeFormContext );
  const { stepTwoForm, rubricSelected} = useContext( StepTwoFormContext );

  const [ commertialConditions, setCommertialConditions ] = useState<GetCommertialsConditionsResponse>();
  const [ commerceQuotationComponents, setCommerceQuotationComponents ] = useState<Array<CommerceQuotationComponent>>([]);
  const [ quotationResponse, setQuotationResponse ] = useState<WebCommerceQuotation>();
  const [ labelButton, setLabelButton ] = useState<QuotationButton>( quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR );
  const [quotationTabs, setQuotationTabs] = useState<Array<any>>([]);
  const [activeTab, setActiveTab] = useState(1);

  const getSavedBienByTab = useCallback(
    (tabKey: string | number) => {
      const quotation = savedQuotation ?? quotationToRenewal;

      return quotation?.bienes?.find(
        (bien: any, index: number) =>
          Number(bien.numeroComponente ?? index + 1) === Number(tabKey)
      );
    },
    [savedQuotation, quotationToRenewal]
  );

  const generateCoveragesInfo = useCallback(( tabKey: string ): Array<QuotationCoverage> => {
    const quotationCoverages: Array<QuotationCoverage> = [];
    
    const allFields = stepFourCommerceQuotationForm.getFieldsValue();

    const allCheckboxFields = Object.keys(allFields).filter((el) => el.includes('checkbox'));

    const allCheckboxFieldByTab = allCheckboxFields.filter( el => el.includes(`commerce-tab-${tabKey}`));

    const savedBien = getSavedBienByTab(tabKey);

    const planSelected =
      stepThreeForm?.getFieldValue(`plan-tab-${tabKey}`) ??
      savedBien?.planSeleccionado?.toString();

    const riskCode =
      rubricSelected?.codigoDeRiesgo ??
      savedBien?.coberturasSeleccionadas?.[0]?.codigoRiesgo;

    const coveragesByTab = allCoveragesPerPlan
      ?.find((el) => String(el.plan) === String(planSelected))
      ?.coverages?.filter((coverage) =>
        riskCode ? String(coverage.codigoRiesgo) === String(riskCode) : true
      );

    allCheckboxFieldByTab.forEach((el) => {
      if (stepFourCommerceQuotationForm.getFieldValue(el)) {
        const { id, activityKey } = getCoverageAndActivityKeyByID( el );

        const coverage = coveragesByTab?.find( el => el.id === id);

        if (!coverage) return;

        const sumaAseguradaInputName = generateInputID( id, activityKey );
        
        const primaInputName = generateInputID( id, activityKey, 'prima' );

        quotationCoverages.push({
          cobertura: coverage.codigo!,
          riesgo: coverage.codigoRiesgo!,
          sumaAsegurada: stepFourCommerceQuotationForm.getFieldValue( sumaAseguradaInputName ),
          prima: stepFourCommerceQuotationForm.getFieldValue( primaInputName ) ?? '0',
          pormilaje: '0',
        });
      }
    });

    return quotationCoverages;
  }, [stepFourCommerceQuotationForm, allCoveragesPerPlan, stepThreeForm, rubricSelected, getSavedBienByTab]);

  const generateFeaturesByTab = useCallback(( planCode: string, tabKey: number ): Array<PropertyFeature> => {
    const features = allFeatures.find( el => el.planCode === planCode )?.features ?? [];

    const featuresByTab: Array<PropertyFeature> = features.map( el => ({
      aplica: stepThreeForm?.getFieldValue(`feature-tab-${tabKey}-${el.codigo}-${el.codigoEquivalente}`) ? 'S' : 'N',
      codigo: el.codigo,
      codigoEquivalente: el.codigoEquivalente,
      descripcion: el.descripcion,
      incluyeWeb: 'S',
      tiene: stepThreeForm?.getFieldValue(`feature-tab-${tabKey}-${el.codigo}-${el.codigoEquivalente}`) ? 'S' : 'N',
    }));

    return featuresByTab;
  }, [allFeatures, stepThreeForm]);

  const generateCommerceComponentsInfo = useCallback((): Array<CommerceQuotationComponent> => {
    let components: Array<CommerceQuotationComponent> = [];

    const quotation = savedQuotation ?? quotationToRenewal;

    tabs.forEach(({ key }: any) => {
      const savedBien = getSavedBienByTab(key);

      const location: string = stepThreeForm?.getFieldValue(`location-tab-${key}`);
      const provinceCode: string = stepThreeForm?.getFieldValue(`province-tab-${key}`); 
      const planSelected: string =
        stepThreeForm?.getFieldValue(`plan-tab-${key}`) ??
        savedBien?.planSeleccionado?.toString();
      
      const localitiesOfProvince = allLocalitiesPerProvince.find( el => el.provinceCode === provinceCode )?.localities;

      const riskZone = localitiesOfProvince?.find( el => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location)?.zonaRiesgosVarios ?? '';

      const featuresByTab: Array<PropertyFeature> = generateFeaturesByTab( planSelected, key );

      const component: CommerceQuotationComponent = {
        nroComponente: key,
        codPlan: Number(planSelected),
        tipoVivienda: 0, //* es 0 para polizas nuevas.
        caracteristicasBien: featuresByTab,
        cantCaracteristicas: featuresByTab.length,
        sufijoCodigoPostal: location[0],
        codigoPostal: location.slice(1),
        zonaRiesgo: riskZone,
        buenResultado: 0, //* es 0 para polizas nuevas.
        cobertura: generateCoveragesInfo( key ),
        psua: 0, //* es 0 para polizas nuevas.
        capTarifa: rubricSelected?.capituloTarifa ?? quotation?.capituloDeTarifa,
        capTarifaInc1: rubricSelected?.capituloTarifaInciso1 ?? quotation?.capituloDeTarifaInciso1,
        capTarifaInc2: rubricSelected?.capituloTarifaInciso2 ?? quotation?.capituloDeTarifaInciso2,
        clasificacionRiesgo: 'A', 
      };

      components.push( component );
    });
    
    setCommerceQuotationComponents( components );

    return components;
  }, [ tabs, stepTwoForm, allLocalitiesPerProvince, stepThreeForm, generateCoveragesInfo, generateFeaturesByTab, setCommerceQuotationComponents, rubricSelected, savedQuotation, quotationToRenewal, getSavedBienByTab ]);

  const generateCommerceQuotationForm = useCallback((): CommerceQuotationRequest => {
    const components = generateCommerceComponentsInfo();
    const paymentMethodCode = commercePaymentMethodsForm.getFieldInstance('paymentMethod') ? commercePaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1] : getValueDefaultPaymentMethod(paymentMethodsList)
    const administrativeSurcharge = commercePaymentMethodsForm.getFieldInstance('extraVariable') ? commercePaymentMethodsForm?.getFieldValue('extraVariable') : commertialConditions?.porcentajeExtraPrimaVariable

    return {
      token: quotationInfo?.token!,
      arse: quotationInfo?.arse!,
      rama: quotationInfo?.branch!,
      codigoTipoFormaPago: paymentMethodCode,
      cantComponentes: components.length,
      cliente: clientSelected!,
      componentes: components,
      recargoAdministrativo: administrativeSurcharge
    }
  }, [quotationInfo, clientSelected, generateCommerceComponentsInfo, commercePaymentMethodsForm, commertialConditions, paymentMethodsList]);

  const clearPrimaFieldsOnFormChange = useCallback(() => {
    const allFields = stepFourCommerceQuotationForm.getFieldsValue()
    const allPrimaKeys = Object.keys( allFields ).filter( el => el.includes('prima') );
    stepFourCommerceQuotationForm.resetFields( allPrimaKeys );
  }, [ stepFourCommerceQuotationForm ]);

  const getCommertialsConditions = useCallback(async () => {
    try {
      if (!commertialConditions) {
        const response = await QuotationService.getComertialsConditions( quotationInfo?.branch! );

        if (response.data) setCommertialConditions( response.data );
      }
    } catch {}
  }, [quotationInfo, setCommertialConditions]);

  useEffect(() => {
    getCommertialsConditions();
  }, [getCommertialsConditions]);

  const createTabs = useCallback(() => {
    const newTabs = tabs?.map(({ key, label }: any) => ({
      children: <CommerceQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label,
    }));

    setQuotationTabs(newTabs);

    setPlansSelectedByTabs(tabs, stepThreeForm);
  }, [tabs, setQuotationTabs, setPlansSelectedByTabs, stepThreeForm]);


  const updateFieldsBeforeQuotation = useCallback(
    ({ componentes }: WebCommerceQuotation) => {
      const paymentMethodCode =
        commercePaymentMethodsForm.getFieldValue('paymentMethod');
      const defaultPaymentMethod = paymentMethodsList?.find(
        (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
      );

      if (
        paymentMethodCode &&
        paymentMethodsList?.find((el) => `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode)
      ) {
        commercePaymentMethodsForm.setFieldValue(
          'paymentMethod',
          paymentMethodCode
        );
      } else {
        commercePaymentMethodsForm.setFieldValue(
          'paymentMethod',
          `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`
        );
      }

      if (
        !commercePaymentMethodsForm.getFieldValue('commission') &&
        !commercePaymentMethodsForm.getFieldValue('extraVariable')
      ) {
        commercePaymentMethodsForm.setFieldValue(
          'commission',
          Number(commertialConditions?.porcentajeDeComision)
        );
        commercePaymentMethodsForm.setFieldValue(
          'extraVariable',
          Number(commertialConditions?.porcentajeExtraPrimaVariable)
        );
      }

      componentes.forEach(({ nroDeComponente, coberturas }) => {
        coberturas.forEach(({ prima, id }) => {
          const primaInputName = generateInputID(
            id!,
            `commerce-tab-${nroDeComponente}`,
            'prima'
          );

          stepFourCommerceQuotationForm.setFieldValue(
            primaInputName,
            Number(prima)
          );
        });
      });
    },
    [
      commercePaymentMethodsForm,
      paymentMethodsList,
      commertialConditions,
      stepFourCommerceQuotationForm,
      allCoveragesPerPlan,
    ]
  );

  const handlerOnClickQuotation = useCallback(
    async (form: CommerceQuotationRequest) => {
      setCanShowSpinner(true);      
      try {
        await stepFourCommerceQuotationForm.validateFields();

        const response = await QuotationService.generateCommerceQuotation(form);

        if (response.data) {
          scrollToEmitButton();
          setLabelButton(QUOTATION_BUTTON.EMITIR);

          const tabsUserCanNotDelete: Array<number> = [];

          response.data.cotizacionWebComercio.componentes.forEach(
            ({ nroDeComponente, coberturas }) => {
              const planSelected = stepFourCommerceQuotationForm.getFieldValue(
                `plan-tab-${nroDeComponente}`
              );

              const coveragesPerPlan =
                allCoveragesPerPlan.find(
                  (el) => (el.plan as string) === String(planSelected)
                )?.coverages ?? [];

              coberturas?.forEach((coverage) => {
                const originalCoverage = coveragesPerPlan.find(
                  (item) =>
                    item.codigo === coverage.codigoCobertura &&
                    item.codigoRiesgo === coverage.codigoRiesgo
                );
                coverage.id = originalCoverage?.id;
                coverage.subCategoriaDeTextos = originalCoverage?.subCategoriaDeTextos;
              });
              tabsUserCanNotDelete.push(Number(nroDeComponente));
            }
          );

          setTabsUserCanNotDelete(tabsUserCanNotDelete);
          setQuotationResponse(response.data.cotizacionWebComercio);
          updateFieldsBeforeQuotation(response.data.cotizacionWebComercio);
          return;
        };
      } catch (error: any) {
        if (error && error?.errorFields?.length > 0) {
          handleTabErrors(error?.errorFields, ['tab'], 'Incendio Web');
        }
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      stepFourCommerceQuotationForm,
      setLabelButton,
      setCanShowSpinner,
      setQuotationResponse,
      updateFieldsBeforeQuotation,
      setTabsUserCanNotDelete,
    ]
  )

  const handlerOnClickEmit = useCallback(async () => {
    setCurrentStep(QuotationStep.FIVE);
  }, [setCurrentStep]);

  const handlerOnClick = useCallback(async () => {
    try {
      const form: CommerceQuotationRequest = generateCommerceQuotationForm();      
      if (labelButton === QUOTATION_BUTTON.EMITIR) {
        return await handlerOnClickEmit();
      }

      if (labelButton === QUOTATION_BUTTON.COTIZAR || labelButton === QUOTATION_BUTTON.RECOTIZAR)
        return await handlerOnClickQuotation(form);
    } catch (error) {}
  }, [
    labelButton,
    generateCommerceQuotationForm,
    handlerOnClickEmit,
    handlerOnClickQuotation,
  ]);

  const fillStepFourServices = useCallback(async () => {
    getCommertialsConditions();
    setPlansSelectedByTabs(tabs, stepThreeForm);
  }, [
    getCommertialsConditions,
    setPlansSelectedByTabs,
    tabs,
    stepThreeForm,
  ]);

  const fillStepFourData = useCallback(async (isRenewal:boolean) => {


    let quotation:any;
    if (isRenewal) {
      quotation = quotationToRenewal;
    }else{
      quotation = savedQuotation;
    }
    const newTabs = tabs?.map(({ key, label }: any) => ({
      children: <CommerceQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label,
    }));

    setQuotationTabs(newTabs);

    //* Se setea vacio ya que no tenemos la primera respuesta de la COTIZACIÓN en un retome. 
    setQuotationResponse({
      sumaAsegurada: "",
      premio: "",
      prima: "",
      componentes: quotation.bienes.map((bien: any) => {
        
        return {
          nroDeComponente: bien.numeroComponente,
          producto: "",
          tipoVivienda: "",
          codigoPostal: "",
          sufijoCodigoPostal: "",
          zonaRiesgo: "",
          buenResultado: "",
          sumaAsegurada: "",
          premio: "",
          requiereInspeccion: "",
          porcentajeSumaAsegurada: "",
          capituloTarifa: "",
          capituloTarifaInc1: "",
          agravamiento: "",
          caracteristicas: quotation.caracteristicasSeleccionadas,
          coberturas: quotation.coberturasSeleccionadas,
          impuestos: [
            {
              codigoCobertura: "",
              impComision: "",
              impExtraPrimaFija: "",
              impImpuestosInternos: "",
              impIva: "",
              impIvaPercepcion: "",
              impIvaRnl: "",
              impIibbPercepcion: "",
              impIibbRiesgo: "",
              impIibbEmpresa: "",
              porImpuestosInternos: "",
              porIva: "",
              porIvaRnl: "",
              porIvaPercepcion: "",
              porTasaSsn: "",
              porServiciosSociales: "",
              impExtraPrimaVar: "",
              impRecargoFinanciero: "",
              selladoDeEmpresa: "",
              selladoDelRiesgo: "",
              impServiciosSociales: "",
              impTasaSsn: "",
              porComision: "",
              porExtraPrimaVar: "",
              porRecargoFinanciero: "",
              calculoImpuestos: ""
            }
          ],
          bonificaciones: []
        }
      }),
      impuestos: {
        rama: "1",
        cantPolizaXRama: "",
        prima: "",
        porRecargoFinanciero: "",
        impRecargoFinanciero: "",
        impExtraPrimaFija: "",
        impSubtotalPrima: "",
        selladoDelRiesgo: "",
        selladoDeEmpresa: "",
        porImpuestosInternos: "",
        impImpuestosInternos: "",
        porServiciosSociales: "",
        impServiciosSociales: "",
        porTasaSsn: "",
        impTasaSsn: "",
        impIibbPercepcion: "",
        impIibbEmpresa: "",
        impIibbRiesgo: "",
        impIvaRnl: "",
        porIvaRnl: "",
        impIva: "",
        porIva: ""
      },
      condicionesComerciales: {
        cantPolizaXRama: "",
        porRecargoAdminist: "",
        impRecargoAdminist: "",
        porComisionProd: "",
        impComision: "",
        porRecargoAdminist1: "",
        porComisionProd1: ""
      },
      condicionesComerciales1: {
        rama: "1",
        porcRecargoAdminist: ""
      }
    });
    
    quotation.bienes.forEach((bien: any, index:number) => {
      const _tab = index + 1;

      bien.coberturasSeleccionadas.forEach((cobertura: any) => {                
        stepFourCommerceQuotationForm.setFields([
          { name: `checkbox_${cobertura.codigo}-${cobertura.codigoRiesgo}_commerce-tab-${_tab}`, value: true },
          { name: `prima_${cobertura.codigo}-${cobertura.codigoRiesgo}_commerce-tab-${_tab}`, value: cobertura.prima },
          { name: `sumaAsegurada_${cobertura.codigo}-${cobertura.codigoRiesgo}_commerce-tab-${_tab}`, value: cobertura.sumaAsegurada },
        ]);
      });
    });
    
    commercePaymentMethodsForm.setFields([
      { name: `paymentMethod`, value: `${quotation.formaDePago.codigo}-${quotation.formaDePago.codigoDePlanDePagos}` },
      { name: `commission`, value: quotation.formaDePago.porcentajeComision },
      { name: `extraVariable`, value: quotation.formaDePago.porcentajeExtraPrimaVariable },
      { name: `variation`, value: quotation.formaDePago.porcentajeComision - commertialConditions!.porcentajeDeComision }
    ]);

    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
  }, [tabs, savedQuotation, setQuotationTabs, commertialConditions, quotationToRenewal]);

  useEffect(() => {
    if(removedTabIndexes.length && currentStep === QuotationStep.FOUR && formValues) {
      updateFormsAfterRemoveTabs(removedTabIndexes, [stepFourCommerceQuotationForm], formValues);
      setRemovedTabIndexes([]);
    };
  }, [removedTabIndexes, currentStep, formValues]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADING);
      fillStepFourServices();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADING &&
      commertialConditions
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADED);
    }
  }, [
    loadSaveQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_DATA_FILLING);
      fillStepFourData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_DATA_FILLING &&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      commertialConditions &&
      allCoveragesPerPlan.length > 0
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_DATA_FILLED);
    }
  }, [
    loadSaveQuotationState,
    savedQuotation,
    quotationTabs,
    tabs,
    labelButton,
    commertialConditions,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_FOUR_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING);
      setLoadingSavedQuotation(false);
      setCurrentStep(QuotationStep.FOUR);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING) {
      handlerOnClick();
    }
  }, [
    loadSaveQuotationState,
  ]);

  /**
   * * Arranca renovacion
   */

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADING_RENEWAL);
      fillStepFourServices();
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADING_RENEWAL&&
      commertialConditions
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_DATA_FILLING_RENEWAL);
      fillStepFourData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_DATA_FILLING_RENEWAL&&
      quotationTabs.length === tabs.length &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      commertialConditions &&
      allCoveragesPerPlan.length > 0
    ) {      
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_DATA_FILLED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    quotationToRenewal,
    quotationTabs,
    tabs,
    labelButton,
    commertialConditions,
    allCoveragesPerPlan,
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_FOUR_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL);
      setLoadingRenewalQuotation(false);
      setCurrentStep(QuotationStep.FOUR);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL) {      
      handlerOnClick();
    }
  }, [
    renewalQuotationState,
  ]);

  const values = useMemo(
    () => ({
      stepFourCommerceQuotationForm,
      commercePaymentMethodsForm,
      labelButton,
      setLabelButton,
      quotationResponse,
      setQuotationResponse,
      generateCommerceQuotationForm,
      commerceQuotationComponents,
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
      createTabs,
      handlerOnClickQuotation,
      handlerOnClick,
    }),
    [
      stepFourCommerceQuotationForm,
      commercePaymentMethodsForm,
      labelButton,
      setLabelButton,
      quotationResponse,
      setQuotationResponse,
      generateCommerceQuotationForm,
      commerceQuotationComponents,
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      quotationTabs,
      setQuotationTabs,
      activeTab,
      setActiveTab,
      createTabs,
      handlerOnClickQuotation,
      handlerOnClick,
    ]
  );

  return (
    <StepFourFormContext.Provider value={values}>
      {children}
    </StepFourFormContext.Provider>
  );
};
